import Image from 'next/image'
import {FC} from 'react'

import {getContactContent} from '@/content/contact'
import {useFooterContent} from '@/content/footer'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {trackClickOnContactEmail} from '@/utils/tracking/helpers'

import {Text, TextFontWeight, TextVariant} from '../common/content/Text'
import {PaymentMethods} from '../payments/PaymentMethods'

export const PreFooter: FC<React.PropsWithChildren<unknown>> = () => {
  const language = useLanguage()
  const {t} = useTranslation('page')

  const {menu, contact, paymentTitle, offices, partners} = useFooterContent()
  const {emailAddress, emailAddressHref, socials} = getContactContent()

  return (
    <>
      <div className="pre-footer">
        <div className="content">
          <div className="wrapper">
            {menu.map(({title, links}) => (
              <div key={title} className="box">
                <Text fontWeight={TextFontWeight.bold} color={theme.palette.text.primary}>
                  {t(title)}
                </Text>
                <ul className="list">
                  {links.map(
                    ({linkHref, linkText, trackedEvent, prefetch}) =>
                      linkHref &&
                      linkText && (
                        <li key={linkHref} className="item">
                          <Text variant={TextVariant.text14}>
                            <Link href={linkHref} prefetch={prefetch} legacyBehavior>
                              <a
                                className="link"
                                onClick={() => trackedEvent && analytics.track(trackedEvent)}
                                role="link"
                                tabIndex={0}
                              >
                                {t(linkText)}
                              </a>
                            </Link>
                          </Text>
                        </li>
                      ),
                  )}
                </ul>
              </div>
            ))}
            <div className="box">
              <Text fontWeight={TextFontWeight.bold} color={theme.palette.text.primary}>
                {t(contact.title)}
              </Text>
              <ul className="list">
                <li className="item">
                  <Text variant={TextVariant.text14}>
                    <a
                      className="link"
                      href={emailAddressHref}
                      onClick={() => trackClickOnContactEmail({position: 'Footer'})}
                    >
                      {emailAddress}
                    </a>
                  </Text>
                </li>
                <li className="item">
                  <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                    {t(contact.businessHours)}
                  </Text>
                </li>
                <li className="item">
                  <ul className="social">
                    {Object.values(socials).map(({imageSrc, imageAlt, linkHref}) => (
                      <li key={linkHref}>
                        <a href={linkHref} target="_blank" rel="noopener noreferrer">
                          <Image src={imageSrc} alt={imageAlt} width={20} height={20} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </div>
            <div className="box">
              <Text fontWeight={TextFontWeight.bold} color={theme.palette.text.primary}>
                {t(paymentTitle)}
              </Text>
              <div className="list payment-cards">
                <PaymentMethods supportedCards={['visa', 'mastercard', 'amex']} />
              </div>
              <div className="revolut-logo">
                <Image
                  src="/images/partners/revolut-logo.svg"
                  alt="Revolut logo"
                  width={64}
                  height={24}
                  style={{objectFit: 'contain'}}
                />
              </div>
            </div>
          </div>
          <div className="offices">
            <Text fontWeight={TextFontWeight.bold} color={theme.palette.text.primary}>
              {t(offices.title)}
            </Text>
            <ul className="list">
              {offices.list.map(({country, street, city, phoneNumber, phoneNumberHref}) => (
                <li key={country} className="office">
                  <div className="address">
                    <Text
                      variant={TextVariant.text14}
                      color={theme.palette.text.white}
                      className={TextFontWeight.bold}
                    >
                      {alpha2ToName(country, language)}
                    </Text>
                  </div>
                  <div className="address">
                    <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                      {t(street)}
                    </Text>
                  </div>
                  <div className="address">
                    <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                      {t(city)}
                    </Text>
                  </div>
                  {phoneNumber && (
                    <div className="address">
                      <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                        <a className="link" href={phoneNumberHref}>
                          {phoneNumber}
                        </a>
                      </Text>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="partners">
          <Text fontWeight={TextFontWeight.bold} color={theme.palette.text.primary}>
            {t(partners.title)}
          </Text>
          <ul className="list">
            {partners.list.map(({imageSrc, imageAlt, imageWidth, imageHeight, linkHref}) => (
              <li key={linkHref}>
                <a href={linkHref} target="_blank" rel="noopener noreferrer">
                  {/* Intrinsic width/height are declared so the browser can reserve the right box
                      before load — CSS scales these to height: 24px. Without them this list is the
                      page's only unsized-images failure. */}
                  <Image
                    src={imageSrc}
                    alt={imageAlt}
                    width={imageWidth}
                    height={imageHeight}
                    style={{width: 'auto', height: '24px'}}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style jsx>{`
        .pre-footer {
          padding: ${theme.spacing[6]} 0;
        }

        .content {
          display: flex;
          justify-content: space-between;
          margin-bottom: ${theme.spacing[5.5]};
        }

        .wrapper {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          max-width: 550px;
          gap: ${theme.spacing[5.5]};
        }

        .box {
          width: 100%;
          max-width: 220px;
        }

        .list {
          margin-top: ${theme.spacing[2]};
        }

        .item + .item {
          margin-top: ${theme.spacing[1]};
        }

        .link {
          color: ${theme.palette.text.white};
        }

        .link:hover,
        .link:focus {
          text-decoration: underline;
        }

        .social {
          display: flex;
          margin-top: ${theme.spacing[2]};
          gap: ${theme.spacing[2.5]};
        }

        .payment-cards {
          margin-top: ${theme.spacing[2.5]};
        }

        .revolut-logo {
          display: inline-flex;
          border: 1px solid ${theme.palette.background.default};
          border-radius: 4px;
          padding: ${theme.spacing[1]} ${theme.spacing[2]};
          margin-top: ${theme.spacing[3]};
        }

        .offices {
          max-width: 440px;
        }

        .offices .list {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: ${theme.spacing[4.5]};
        }

        .office {
          flex: 0 0 195px;
        }

        .address + .address {
          margin-top: ${theme.spacing[1]};
        }

        .partners .list {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: ${theme.spacing[7]};
        }

        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            max-width: 55%;
            gap: ${theme.spacing[5]};
            justify-content: flex-start;
          }

          .offices {
            max-width: 45%;
          }

          .office {
            flex: 0 0 175px;
          }
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .content {
            flex-direction: column;
            gap: ${theme.spacing[5.5]};
          }

          .wrapper {
            justify-content: center;
          }

          .wrapper,
          .offices {
            max-width: inherit;
          }

          .box {
            max-width: 35%;
            text-align: center;
          }

          .list {
            margin-top: ${theme.spacing[3]};
          }

          .item + .item {
            margin-top: ${theme.spacing[2]};
          }

          .social {
            justify-content: center;
            margin-top: ${theme.spacing[2]};
          }

          .payment-cards {
            display: flex;
            justify-content: center;
          }

          .offices {
            text-align: center;
          }

          .offices .list {
            justify-content: center;
          }

          .address + .address {
            margin-top: ${theme.spacing[1]};
          }

          .partners {
            text-align: center;
          }

          .partners .list {
            justify-content: center;
            gap: ${theme.spacing[5]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            align-items: center;
            gap: ${theme.spacing[4]};
          }

          .content {
            gap: ${theme.spacing[4]};
            margin-bottom: ${theme.spacing[4]};
          }

          .box {
            flex: 1 0 100%;
            max-width: 100%;
          }

          .list {
            margin-top: ${theme.spacing[2]};
          }

          .offices .list {
            gap: ${theme.spacing[4]};
          }

          .office {
            flex: 1 0 100%;
          }

          .partners .list {
            margin-top: ${theme.spacing[3]};
            row-gap: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
